import { memo } from 'react';

import { isMobile } from 'hooks';
import { Drawer } from 'components/drawer';
import { formatPrice } from 'utils/helpers';
import { formatCalendarDate } from 'utils/date';
import { EventPayment } from 'store/api/event/types';
import {
  eventPaymentTypeLabel,
  eventPaymentTransactionLabel,
} from 'store/api/event';

import * as LineStyles from '../line-check/styles';
import { RecordTable, RecordColumn } from '../record-table';
import {
  sumAmounts,
  LineDifference,
  useFitDrawerTop,
  LineDrawerHeader,
  useEscapeToClose,
} from '../line-check';

import { LocationRentDrawerProps } from './types';

// Every column the event-payments list had; Notes is the flexible column.
const columns: RecordColumn<EventPayment>[] = [
  {
    key: 'date',
    kind: 'date',
    title: 'Date',
    // The saved day in every timezone (as the event-payments list).
    value: (row) => row.date && formatCalendarDate(row.date),
  },
  { key: 'notes', kind: 'flex', title: 'Notes', value: (row) => row.notes },
  {
    key: 'type',
    kind: 'nowrap',
    title: 'Payment Type',
    value: (row) => eventPaymentTypeLabel[row.type],
  },
  {
    kind: 'nowrap',
    key: 'transaction',
    title: 'Transaction',
    value: (row) =>
      row.transaction && eventPaymentTransactionLabel[row.transaction],
  },
  {
    key: 'paidBy',
    kind: 'compact',
    title: 'Paid/Scheduled By',
    value: (row) => row.paidBy?.name,
  },
  {
    key: 'method',
    kind: 'nowrap',
    title: 'Payment Method',
    value: (row) => row.paymentMethod?.name,
  },
  {
    key: 'cashed',
    kind: 'nowrap',
    title: 'Cashed',
    value: (row) => (row.cashed ? 'Yes' : 'No'),
  },
  {
    key: 'amount',
    kind: 'amount',
    title: 'Amount',
    value: (row) => formatPrice(row.amount),
  },
];

export const LocationRentDrawer = memo(
  ({
    opened,
    onClose,
    eventName,
    lineAmount,
    locationRent,
    title = 'Locations Rent',
  }: LocationRentDrawerProps) => {
    const mobile = isMobile();
    const { ref, topOffset } = useFitDrawerTop(mobile);
    useEscapeToClose(!!opened && !!locationRent?.length, onClose);

    if (!locationRent?.length) {
      return null;
    }

    return (
      <Drawer elevated hideOverlay opened={opened} topOffset={topOffset}>
        <LineStyles.DrawerContent ref={ref}>
          <LineDrawerHeader
            title={title}
            onClose={onClose}
            subtitle={eventName}
          />
          <RecordTable
            columns={columns}
            rows={locationRent}
            getKey={(row) => row.id}
            total={sumAmounts(locationRent)}
          />
          <LineDifference
            lineAmount={lineAmount}
            total={sumAmounts(locationRent)}
          />
        </LineStyles.DrawerContent>
      </Drawer>
    );
  }
);
